// ** React Imports
import { useContext, useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";

// ** Store & Actions
import { useDispatch, useSelector } from "react-redux";
import { getUserProfileMain } from "store/modules/user-profile/actions/get-user-profile-main.action";

// ** Third Party Components
import loadable from "@loadable/component";
import styled from "styled-components";

// ** Custom Components
import Block from "components/layout/Block";
import Spacer from "components/layout/Spacer";
import NavItemLink from "components/base/NavItemLink";
import Panel from "components/layout/Nav/Panel";
import List from "components/layout/Nav/List";
import Item from "components/layout/Nav/Item";
import { Condition } from "components/conditional";
import PresentationTemplateContainer from "components/layout/PresentationTemplate/PresentationTemplateContainer/PresentationTemplateContainer";
import { ButtonSecondary } from "components/layout/Button/ButtonSecondary";

const ActionSection = loadable(() =>
	import("components/layout/PresentationTemplate/ActionSection/ActionSection")
);

// ** User Components
import navigationItemsFn from "../navigationItems";

// ** Custom Hooks
import useActionItems from "./hooks/useActionItems";

// ** Contexts
import authContext from "contexts/authContext";
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styles Import
import { breakpoints } from "styles/grid";

// ** SVG Static Imports
import ElipsisHorizontal from "components/icons/ElipsisHorizontal";

// #####################################################

const StyledNavigationContainer = styled((props) => (
	<Block flex flexWrap {...props} />
))`
	nav,
	li {
		border: 0;
	}

	@media (max-width: ${breakpoints.phone - 1}px) {
		flex: 1;

		ul {
			display: grid;
			grid-template-columns: 25% 25% 25% 25%;
			min-height: 56px;
		}
	}
`;

// #####################################################

const UserNavSection = () => {
	const { isPhone } = useContext(deviceTypeContext);

	const isAuth = useContext(authContext);
	const { login } = useParams();

	const [isActionMenuVisible, setActionMenuVisibility] = useState(false);

	const isAllowed = useSelector((state) => state.userProfileMain.isAllowed);
	const userStatistics = useSelector(
		(state) => state.userProfileMain.statistics
	);

	const dispatch = useDispatch();

	const location = useLocation();
	const currentPathname = location?.pathname ?? "";

	const getProfileDetailsMergeFn = () =>
		dispatch(getUserProfileMain({ login, isAuth, throwError: "true" }));

	const navigationItems = navigationItemsFn({
		isAllowed,
		login,
		userStatistics,
		currentPathname,
		getProfileDetailsMergeFn,
		isPhone,
	});

	const actionItems = useActionItems();

	useEffect(() => {
		if (isActionMenuVisible && isPhone) {
			setActionMenuVisibility(false);
		}
	}, [location.pathname]);

	const renderContent = (item) => {
		const { content, ...rest } = item;
		if (typeof content === "function") {
			return item.content(rest);
		}
		return item.content;
	};

	// #####################################################

	return (
		<Block>
			<PresentationTemplateContainer
				flex
				justifyBetween
				flexWrap
				alignCenter
			>
				<StyledNavigationContainer>
					<Panel appearance="tabs" noMargin noLine>
						<List noPad>
							{navigationItems
								.filter((item) => Boolean(item))
								.map((item) => {
									return (
										<NavItemLink
											container={Item}
											to={item.link}
											key={item.key}
											exact={item.exact}
											onClick={(event) => {
												if (item.disabled) {
													event.preventDefault();
												} else if (item.onClick) {
													item.onClick(event);
												}
											}}
											disabled={item.disabled}
										>
											<Item.Content
												noPad
												flex
												style={{
													alignItems: "centert",
													justifyContent: "center",
												}}
											>
												{renderContent(item)}
											</Item.Content>
										</NavItemLink>
									);
								})}

							<Condition value={actionItems && isPhone}>
								<Item
									expandable
									flex
									style={{
										alignItems: "center",
										justifyContent: "center",
										backgroundColor: "transparent",
									}}
								>
									<Block
										flex
										onClick={() =>
											setActionMenuVisibility(
												(prev) => !prev
											)
										}
										style={{ cursor: "pointer" }}
									>
										<ButtonSecondary
											iconOnly
											svgIconComponent={ElipsisHorizontal}
											icon="ellipsis-h"
										/>
									</Block>
								</Item>
							</Condition>
						</List>
					</Panel>
				</StyledNavigationContainer>
				{!isPhone && <Spacer />}
				<ActionSection
					actionItems={actionItems}
					isActionMenuVisible={isActionMenuVisible}
					setActionMenuVisibility={setActionMenuVisibility}
				/>
			</PresentationTemplateContainer>
		</Block>
	);
};

// #####################################################

export default UserNavSection;

// #####################################################
